/**
 * v3.21-1621 · Team Chat Window — internal employee chat scoped to a client.
 *
 * Never touches:
 *   • client Activity timeline
 *   • client Notes
 *   • Call state
 *   • Client Workspace open state (X only hides this window)
 *
 * Real-time: subscribes to `team_chat.message` broadcast events from
 * the existing `wsService` singleton (already opened in AuthContext).
 * A 4s poll acts as a safety net if the WebSocket is disconnected.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { X, Loader2, Send, MessageCircle, UserPlus, Users } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import wsService from '../services/websocket';
import { useAuth } from '../contexts/AuthContext';
import { useTeamChat } from '../context/TeamChatContext';

const relTime = (iso) => {
  if (!iso) return '';
  const t = new Date(iso);
  const now = new Date();
  const same = t.toDateString() === now.toDateString();
  const hh = String(t.getHours()).padStart(2, '0');
  const mm = String(t.getMinutes()).padStart(2, '0');
  return same
    ? `${hh}:${mm}`
    : `${t.getMonth() + 1}/${t.getDate()} ${hh}:${mm}`;
};

// ----------------------------------------------------------------
// Add-Participant popover
// ----------------------------------------------------------------
function AddParticipant({ threadId, onAdded }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const term = q.trim();
    if (term.length < 2) { setResults([]); return undefined; }
    const h = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get('/team-chats/users/search', { params: { q: term, limit: 12 } });
        setResults(r.data?.users || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(h);
  }, [q, open]);

  // Click-outside closes the popover.
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener('mousedown', h);
    return () => window.removeEventListener('mousedown', h);
  }, [open]);

  const invite = async (u) => {
    setSaving(true);
    try {
      const r = await api.post(`/team-chats/${threadId}/participants`, { user_id: u.id });
      toast.success(`${u.full_name || u.email} joined the chat`);
      onAdded?.(r.data);
      setOpen(false); setQ(''); setResults([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add participant');
    } finally { setSaving(false); }
  };

  return (
    <div ref={boxRef} className="relative">
      <Button size="sm" variant="outline" className="h-7 text-[11px]"
        onClick={() => setOpen((v) => !v)} data-testid="team-chat-add-participant-btn">
        <UserPlus className="h-3 w-3 mr-1" /> Add
      </Button>
      {open && (
        <div className="absolute right-0 mt-1 w-64 rounded-md border border-slate-200 bg-white shadow-lg z-20 p-2"
             data-testid="team-chat-participant-popover">
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search employees…"
            className="w-full text-sm rounded border border-slate-300 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-slate-400"
            data-testid="team-chat-participant-search"
          />
          <div className="mt-1 max-h-52 overflow-y-auto">
            {loading && <div className="text-xs text-slate-500 flex items-center gap-1 py-1"><Loader2 className="h-3 w-3 animate-spin" /> searching…</div>}
            {!loading && q.trim().length >= 2 && results.length === 0 &&
              <div className="text-xs text-slate-500 py-1">No employees found</div>}
            {results.map((u) => (
              <button
                key={u.id}
                type="button"
                disabled={saving}
                onClick={() => invite(u)}
                className="w-full text-left rounded px-2 py-1 hover:bg-slate-100 disabled:opacity-50"
                data-testid={`team-chat-invite-${u.id}`}
              >
                <div className="text-sm font-medium truncate">{u.full_name || u.email}</div>
                <div className="text-[10px] text-slate-500 truncate">{u.email}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ----------------------------------------------------------------
// Main floating chat window
// ----------------------------------------------------------------
export default function TeamChatWindow() {
  const { user } = useAuth();
  const { open, clientId, clientName, closeTeamChat } = useTeamChat();

  const [thread,   setThread]   = useState(null);
  const [messages, setMessages] = useState([]);
  const [text,     setText]     = useState('');
  const [loading,  setLoading]  = useState(false);
  const [sending,  setSending]  = useState(false);
  const scrollRef = useRef(null);

  // Hydrate / create the thread when the window opens.
  useEffect(() => {
    if (!open || !clientId) return undefined;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await api.get('/team-chats', { params: { client_id: clientId } });
        if (cancelled) return;
        setThread(r.data);
        const m = await api.get(`/team-chats/${r.data.id}/messages`, { params: { limit: 200 } });
        if (cancelled) return;
        setMessages(m.data?.messages || []);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || 'Failed to load Team Chat');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, clientId]);

  // Real-time push via existing wsService.
  useEffect(() => {
    if (!thread?.id) return undefined;
    const unsub = wsService.subscribe('team_chat.message', (data) => {
      if (!data || data.thread_id !== thread.id) return;
      setMessages((prev) => {
        if (prev.some((m) => m.id === data.id)) return prev;
        return [...prev, data];
      });
    });
    return unsub;
  }, [thread?.id]);

  // 4s polling safety net — cheap, only while window is open.
  useEffect(() => {
    if (!open || !thread?.id) return undefined;
    const h = setInterval(async () => {
      try {
        const m = await api.get(`/team-chats/${thread.id}/messages`, { params: { limit: 200 } });
        setMessages((prev) => {
          const rows = m.data?.messages || [];
          if (rows.length === prev.length) return prev;
          return rows;
        });
      } catch { /* ignore */ }
    }, 4000);
    return () => clearInterval(h);
  }, [open, thread?.id]);

  // Auto-scroll to bottom on new message.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  const send = useCallback(async () => {
    const content = (text || '').trim();
    if (!content || !thread?.id) return;
    setSending(true);
    try {
      const r = await api.post(`/team-chats/${thread.id}/messages`, { content });
      setMessages((prev) => {
        if (prev.some((m) => m.id === r.data?.id)) return prev;
        return [...prev, r.data];
      });
      setText('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send message');
    } finally { setSending(false); }
  }, [text, thread?.id]);

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const participantsLabel = useMemo(() => {
    const rows = thread?.participants || [];
    if (rows.length === 0) return '';
    return rows.map((p) => (p.full_name || p.email || '').split(' ')[0]).filter(Boolean).join(' · ');
  }, [thread?.participants]);

  const uid = user?.id;
  const titleName = clientName || thread?.client_name || 'Client';

  if (!open || !clientId) return null;
  return (
    <Dialog open onOpenChange={(v) => { if (!v) closeTeamChat(); }}>
      <DialogContent
        className="max-w-lg p-0 gap-0 sm:max-h-[80vh] overflow-hidden"
        data-testid="team-chat-modal"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="px-3 py-2 border-b border-slate-200 sticky top-0 bg-white z-10">
          <DialogTitle className="flex items-center gap-2 text-sm">
            <MessageCircle className="h-4 w-4 text-amber-700" />
            <span className="text-slate-500">Team Chat</span>
            <span className="text-slate-400">·</span>
            <span className="font-semibold text-slate-900 truncate" data-testid="team-chat-client-name">{titleName}</span>
            <div className="ml-auto flex items-center gap-1">
              {thread?.id && <AddParticipant threadId={thread.id} onAdded={setThread} />}
              <Button
                type="button" size="sm" variant="ghost"
                className="h-7 w-7 p-0 hover:bg-slate-100"
                onClick={closeTeamChat}
                title="Close (does not delete history)"
                data-testid="team-chat-close-btn"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </DialogTitle>
          <DialogDescription className="text-[11px] text-slate-500 flex items-center gap-1 truncate">
            <Users className="h-3 w-3 shrink-0" />
            <span className="truncate" data-testid="team-chat-participants">Participants: {participantsLabel || '—'}</span>
          </DialogDescription>
        </DialogHeader>

        <div
          ref={scrollRef}
          className="p-3 space-y-2 h-[52vh] overflow-y-auto bg-slate-50"
          data-testid="team-chat-transcript"
        >
          {loading && (
            <div className="text-xs text-slate-500 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Loading conversation…</div>
          )}
          {!loading && messages.length === 0 && (
            <div className="text-xs text-slate-500 italic text-center py-8" data-testid="team-chat-empty">
              No messages yet.  Start the conversation about {titleName}.
            </div>
          )}
          {messages.map((m) => {
            const mine = m.sender_id === uid;
            return (
              <div key={m.id} className={mine ? 'text-right' : 'text-left'} data-testid={`team-chat-message-${m.id}`}>
                <div className="text-[10px] text-slate-500 mb-0.5">
                  <span className="font-semibold text-slate-700">{m.sender_name || 'Unknown'}</span>
                  <span className="mx-1">·</span>
                  {relTime(m.created_at)}
                </div>
                <div className={
                  mine
                    ? 'inline-block bg-amber-500 text-white px-2 py-1 rounded-md text-sm max-w-[85%] whitespace-pre-wrap text-left'
                    : 'inline-block bg-white border border-slate-200 px-2 py-1 rounded-md text-sm max-w-[85%] whitespace-pre-wrap text-slate-900'
                }>
                  {m.content}
                </div>
              </div>
            );
          })}
        </div>

        <div className="p-2 border-t border-slate-200 bg-white flex items-end gap-1.5">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKey}
            placeholder={`Message the team about ${titleName}…`}
            rows={1}
            className="flex-1 resize-none text-sm rounded-md border border-slate-300 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-500"
            data-testid="team-chat-input"
          />
          <Button
            size="sm"
            onClick={send}
            disabled={!text.trim() || sending}
            className="bg-amber-600 hover:bg-amber-700 text-white h-8"
            data-testid="team-chat-send-btn"
          >
            {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
